import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, get, onValue } from 'firebase/database';
import { database } from '../firebase/firebase';
import { Event } from '../types';
import { mergeLocalHistory } from './useEventHistory';

// EventWithId interface
export interface EventWithId extends Event {
    id: string;
}

interface UseEventsPaginationReturn {
    events: EventWithId[];
    loading: boolean;
    error: string | null;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    refresh: () => Promise<void>;
    totalCount: number;
    filteredCount: number;
}

/**
 * Without a `userId` the list is the events this device has opened, from
 * localStorage. `enabled` holds loading back until Firebase has restored the
 * session: before that a signed-in user looks signed out, and would be shown
 * the device's list instead of their own.
 */
export const useEventsPagination = (
    userId?: string,
    filterStatus: 'all' | 'active' | 'closed' = 'all',
    enabled: boolean = true
): UseEventsPaginationReturn => {
    const [events, setEvents] = useState<EventWithId[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [lastEventId, setLastEventId] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [filteredCount, setFilteredCount] = useState(0);
    const [allEventIds, setAllEventIds] = useState<string[]>([]);
    const [eventStatusMap, setEventStatusMap] = useState<{ [eventId: string]: boolean }>({});

    const ITEMS_PER_PAGE = 5;
    // Guard against concurrent loadEventIds calls causing race conditions
    const loadingIdsRef = useRef(false);

    useEffect(() => {
        const eventListRef = ref(database, 'eventList');
        const unsubscribe = onValue(eventListRef, (snapshot) => {
            const data = snapshot.val();
            setEventStatusMap(data || {});
        });

        return () => unsubscribe();
    }, []);

    // Get all event IDs for the user with filter applied
    const loadEventIds = useCallback(async () => {
        if (loadingIdsRef.current) return; // Prevent concurrent calls
        loadingIdsRef.current = true;
        try {
            let eventIds: string[] = [];

            if (userId) {
                const userEventsRef = ref(database, `userEvents/${userId}`);
                const historyEventsRef = ref(database, `userEventHistory/${userId}`);
                
                const [userEventsSnapshot, historySnapshot] = await Promise.all([
                    get(userEventsRef),
                    get(historyEventsRef)
                ]);

                const userEventIds = userEventsSnapshot.exists() ? Object.keys(userEventsSnapshot.val()) : [];
                const history = await mergeLocalHistory(userId, historySnapshot.val() || {});
                const historyEventIds = Object.keys(history);
                
                eventIds = Array.from(new Set([...userEventIds, ...historyEventIds]));
            } else {
                const localHistory = localStorage.getItem('eventHistory');
                if (localHistory) {
                    try {
                        const parsed = JSON.parse(localHistory);
                        eventIds = Object.keys(parsed || {});
                    } catch (err) {
                        console.error('Error parsing local history:', err);
                    }
                }
            }

            if (eventIds.length > 0) {

                // Get event creation timestamps for sorting
                const eventTimestamps = await Promise.all(
                    eventIds.map(async (eventId) => {
                        try {
                            const eventRef = ref(database, `events/${eventId}/meta/createdAt`);
                            const timestampSnapshot = await get(eventRef);
                            return {
                                eventId,
                                createdAt: timestampSnapshot.exists() ? timestampSnapshot.val() : 0
                            };
                        } catch (err) {
                            console.error(`Error getting timestamp for event ${eventId}:`, err);
                            return { eventId, createdAt: 0 };
                        }
                    })
                );

                // Sort by creation date (newest first)
                eventTimestamps.sort((a, b) => b.createdAt - a.createdAt);
                const sortedEventIds = eventTimestamps.map(item => item.eventId);

                // Apply status filter using eventStatusMap
                const filteredEventIds = sortedEventIds.filter(eventId => {
                    if (filterStatus === 'all') return true;

                    const isActive = eventStatusMap[eventId];
                    const isClosed = !isActive;

                    return filterStatus === 'active' ? !isClosed : isClosed;
                });

                setAllEventIds(filteredEventIds);
                setTotalCount(sortedEventIds.length);
                setFilteredCount(filteredEventIds.length);
                setHasMore(filteredEventIds.length > ITEMS_PER_PAGE);
            } else {
                setAllEventIds([]);
                setTotalCount(0);
                setFilteredCount(0);
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading event IDs:', err);
            setError('Failed to load event list');
        } finally {
            loadingIdsRef.current = false;
        }
    }, [userId, filterStatus, eventStatusMap]);

    // Reset when filter changes
    useEffect(() => {
        setEvents([]);
        setLastEventId(null);
        setHasMore(true);
        if (enabled && Object.keys(eventStatusMap).length > 0) {
            loadEventIds();
        }
    }, [filterStatus, loadEventIds, enabled, eventStatusMap]);

    // Load initial events
    const loadInitialEvents = useCallback(async () => {
        if (allEventIds.length === 0) return;

        setLoading(true);
        setError(null);

        try {
            const eventsToLoad = allEventIds.slice(0, ITEMS_PER_PAGE);
            const loadedEvents = await loadEventsByIds(eventsToLoad);

            // Deduplicate by id as a safety net against race conditions
            const seen = new Set<string>();
            const dedupedEvents = loadedEvents.filter(e => {
                if (seen.has(e.id)) return false;
                seen.add(e.id);
                return true;
            });

            setEvents(dedupedEvents);
            setLastEventId(eventsToLoad[eventsToLoad.length - 1]);
            setHasMore(allEventIds.length > ITEMS_PER_PAGE);
        } catch (err) {
            setError('Failed to load events');
            console.error('Error loading initial events:', err);
        } finally {
            setLoading(false);
        }
    }, [allEventIds]);

    // Load more events
    const loadMore = useCallback(async () => {
        if (!hasMore || loading || !lastEventId) return;

        setLoading(true);
        setError(null);

        try {
            const currentIndex = allEventIds.indexOf(lastEventId);
            const nextEvents = allEventIds.slice(currentIndex + 1, currentIndex + 1 + ITEMS_PER_PAGE);

            if (nextEvents.length > 0) {
                const loadedEvents = await loadEventsByIds(nextEvents);
                setEvents(prev => {
                    // Deduplicate: only add events not already in the list
                    const existingIds = new Set(prev.map(e => e.id));
                    const newEvents = loadedEvents.filter(e => !existingIds.has(e.id));
                    return [...prev, ...newEvents];
                });
                setLastEventId(nextEvents[nextEvents.length - 1]);
                setHasMore(currentIndex + 1 + ITEMS_PER_PAGE < allEventIds.length);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            setError('Failed to load more events');
            console.error('Error loading more events:', err);
        } finally {
            setLoading(false);
        }
    }, [allEventIds, lastEventId, hasMore, loading]);

    // Helper function to load events by IDs
    const loadEventsByIds = async (eventIds: string[]): Promise<EventWithId[]> => {
        const loadedEvents: EventWithId[] = [];

        await Promise.all(
            eventIds.map(async (eventId) => {
                try {
                    // Load event meta and settlement data
                    const eventRef = ref(database, `events/${eventId}`);
                    const eventSnapshot = await get(eventRef);

                    // Load expenses separately
                    const expensesRef = ref(database, `eventExpenses/${eventId}`);
                    const expensesSnapshot = await get(expensesRef);

                    if (eventSnapshot.exists()) {
                        const eventData = eventSnapshot.val();
                        const expenses = expensesSnapshot.exists() ? expensesSnapshot.val() : {};

                        // Combine event data with expenses
                        const completeEvent: Event = {
                            ...eventData,
                            expenses: expenses
                        };

                        loadedEvents.push({ ...completeEvent, id: eventId });
                    }
                } catch (err) {
                    console.error(`Error loading event ${eventId}:`, err);
                }
            })
        );

        // Sort by creation date (newest first) to maintain order
        return loadedEvents.sort((a, b) => b.meta.createdAt - a.meta.createdAt);
    };

    // Refresh function
    const refresh = useCallback(async () => {
        setEvents([]);
        setLastEventId(null);
        setHasMore(true);
        await loadEventIds();
    }, [loadEventIds]);

    // Load events after getting IDs
    useEffect(() => {
        if (allEventIds.length > 0) {
            loadInitialEvents();
        }
    }, [allEventIds, loadInitialEvents]);

    // Note: Initial load and filter-change reset are both handled by the effect above
    // (allEventIds changes trigger loadInitialEvents automatically).
    // The loadEventIds call is triggered by the reset effect below.

    return {
        events,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
        totalCount,
        filteredCount
    };
};